import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { FsPresenceDevice, FsPresenceResponse } from '@lib/api/entities';
import { SnCurrentUser } from '@lib/auth';
import { FsDataDogRumService } from '@lib/data-dog-rum';

import { AxonDevicePopoutComponent } from '../components';
import { DEVICE_PAGINATION } from '../constants';
import { IDeviceMarker } from '../interfaces';
import { IDevice } from '../interfaces/device.interface';
import { logPresenceApiResponse, ThrottleKey } from '../utils/presence-logging.utils';
import { FsAxonService } from './axon.service';

interface IActiveIFrames {
  iFrame: HTMLIFrameElement;
  axonDeviceId: string;
  partnerId: string;
}

@Injectable({
  providedIn: 'root',
})
export class PresenceService {
  get openPopout$(): Observable<IDevice> {
    return this._openPopout$.asObservable();
  }

  get enterFollowMode$(): Observable<IDeviceMarker> {
    return this._enterFollowMode$.asObservable();
  }

  get leaveFollowMode$(): Observable<IDeviceMarker> {
    return this._leaveFollowMode$.asObservable();
  }

  get bodyCameraDocked$(): Observable<IDevice> {
    return this._bodyCameraDocked$.asObservable();
  }

  get orgSpecificBaseUrl(): string {
    return `${this._axonService.rtoApiGatewayUrl}/presence`;
  }

  get getHeaders(): { accept: string; clientType: string; authorization: string } {
    const accessToken =
      this._currentUser.settings.authTokens?.axonconnect?.accessToken || this.token;
    return {
      accept: 'application/geo+json',
      clientType: 'INTERNAL_BROWSER',
      authorization: `Bearer ${accessToken}`,
    };
  }

  public domain: string;
  public token: string;
  public deviceInFollowMode: IDeviceMarker;

  public activePopouts: MatDialogRef<AxonDevicePopoutComponent>[] = [];
  public activeIFrames: IActiveIFrames[] = [];

  private readonly _leaveFollowMode$ = new BehaviorSubject<IDeviceMarker>(undefined);
  private readonly _enterFollowMode$ = new BehaviorSubject<IDeviceMarker>(undefined);
  private readonly _openPopout$ = new BehaviorSubject<IDevice>(undefined);
  private readonly _bodyCameraDocked$ = new BehaviorSubject<IDevice>(null);

  constructor(
    private readonly _axonService: FsAxonService,
    private readonly _currentUser: SnCurrentUser,
    private readonly _httpClient: HttpClient,
    private readonly _datadogService: FsDataDogRumService,
  ) {
    this.domain = this._currentUser.settings.authTokens?.axonconnect?.domain;
    this.token = this._currentUser.settings.authTokens?.axonconnect?.accessToken;
    this._bbox$.subscribe((bbox) => {
      this._updatedBbox = bbox;
    });
    this._zoomLevel$.subscribe((zoomLevel) => {
      this._updatedZoomLevel = zoomLevel;
    });
  }

  private readonly _bbox$ = new BehaviorSubject<string[]>([]);
  private readonly _zoomLevel$ = new BehaviorSubject<number>(4);
  private _updatedBbox: string[] = [];
  private _updatedZoomLevel: number = 4;

  public getPresenceDevices(
    partnerId: string,
    providerName: string,
    bbox: string[],
    zoom: number,
  ): Observable<FsPresenceDevice[]>;

  public getPresenceDevices(
    partnerId: string,
    providerName: string,
    bbox: string[],
    zoom: number,
    includeStatus: true,
  ): Observable<{
    devices: FsPresenceDevice[];
    status: number;
    correlationId?: string;
    partnerId?: string;
  }>;

  public getPresenceDevices(
    partnerId: string,
    providerName: string,
    bbox: string[],
    zoom: number,
    includeStatus: boolean = false,
  ): Observable<
    | FsPresenceDevice[]
    | { devices: FsPresenceDevice[]; status: number; correlationId?: string; partnerId?: string }
  > {
    const url = `${this.orgSpecificBaseUrl}/v1/partner/${partnerId}/provider/${providerName}/maps/devices/`;
    const headers = this.getHeaders;

    const since = new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString();

    const params = {
      bbox,
      zoom,
      doNotCluster: '*',
      since,
    };

    if (!this.token) {
      return this._currentUser.getSettings().pipe(
        filter((settings) => !!settings?.authTokens?.axonconnect?.accessToken),
        take(1),
        switchMap((settings) => {
          this.token = settings?.authTokens?.axonconnect?.accessToken;
          return includeStatus
            ? this.getPresenceDevices(partnerId, providerName, bbox, zoom, true)
            : this.getPresenceDevices(partnerId, providerName, bbox, zoom);
        }),
      );
    }

    if (includeStatus) {
      return this._httpClient
        .get<FsPresenceResponse>(url, { headers, params, observe: 'response' })
        .pipe(
          map((response) => {
            this._logPresenceApiResponse(
              {
                service: 'map',
                endpoint: 'maps_devices',
                statusCode: response.status,
                totalFeatures: response.body?.features?.length || 0,
                correlationId:
                  response.headers.get('X-Correlation-ID') ||
                  response.headers.get('x-correlation-id') ||
                  undefined,
                agencyId: partnerId,
              },
              'map-maps-devices-response',
            );
            return {
              devices: response.body?.features ?? [],
              status: response.status,
              correlationId:
                response.headers.get('X-Correlation-ID') ||
                response.headers.get('x-correlation-id') ||
                undefined,
              partnerId,
            };
          }),
        );
    }

    return this._httpClient.get<FsPresenceResponse>(url, { headers, params }).pipe(
      map((result) => {
        this._logPresenceApiResponse(
          {
            service: 'map',
            endpoint: 'maps_devices',
            statusCode: 200,
            totalFeatures: result.features?.length || 0,
            agencyId: partnerId,
          },
          'map-maps-devices-response',
        );
        if (result.features.length > 0) {
          return result.features.map((feature) => new FsPresenceDevice(feature));
        }
        return [];
      }),
    );
  }

  public getPresenceDeviceDetails(
    partnerId: string,
    provider: string,
    globalDeviceId: string,
  ): Observable<FsPresenceDevice>;

  public getPresenceDeviceDetails(
    partnerId: string,
    provider: string,
    globalDeviceId: string,
    includeStatus: true,
  ): Observable<{
    device: FsPresenceDevice;
    status: number;
    correlationId?: string;
    partnerId?: string;
  }>;

  public getPresenceDeviceDetails(
    partnerId: string,
    provider: string,
    globalDeviceId: string,
    includeStatus?: boolean,
  ): Observable<
    | FsPresenceDevice
    | { device: FsPresenceDevice; status: number; correlationId?: string; partnerId?: string }
  > {
    const url = `${this.orgSpecificBaseUrl}/v1/partner/${partnerId}/provider/${provider}/devices/${globalDeviceId}/`;
    const headers = this.getHeaders;
    if (!this.token) {
      return;
    }

    if (includeStatus) {
      return this._httpClient.get<FsPresenceDevice>(url, { headers, observe: 'response' }).pipe(
        map((response) => {
          this._logPresenceApiResponse(
            {
              service: 'map',
              endpoint: 'device_details',
              statusCode: response.status,
              totalFeatures: 0,
              correlationId: response.headers.get('X-Correlation-ID') || undefined,
              agencyId: partnerId,
            },
            'map-device-details-response',
          );
          return {
            device: response.body,
            status: response.status,
            correlationId:
              response.headers.get('X-Correlation-ID') ||
              response.headers.get('x-correlation-id') ||
              undefined,
            partnerId,
          };
        }),
      );
    }

    return this._httpClient.get<FsPresenceDevice>(url, { headers }).pipe(
      map((res) => {
        this._logPresenceApiResponse(
          {
            service: 'map',
            endpoint: 'device_details',
            statusCode: 200,
            totalFeatures: 0,
            agencyId: partnerId,
          },
          'map-device-details-response',
        );
        return new FsPresenceDevice(res);
      }),
    );
  }

  public getDeviceList(partnerId: string, provider: string): Observable<FsPresenceResponse>;
  public getDeviceList(
    partnerId: string,
    provider: string,
    includeHeaders: false,
    options?: {
      cursor?: string;
      limit?: number;
    },
  ): Observable<FsPresenceResponse>;
  public getDeviceList(
    partnerId: string,
    provider: string,
    includeHeaders: true,
    options?: {
      cursor?: string;
      limit?: number;
    },
  ): Observable<HttpResponse<FsPresenceResponse>>;
  public getDeviceList(
    partnerId: string,
    provider: string,
    includeHeaders = false,
    options?: {
      cursor?: string;
      limit?: number;
    },
  ): Observable<FsPresenceResponse | HttpResponse<FsPresenceResponse>> {
    if (!this.token) {
      return;
    }

    const params = {
      since: new Date(Date.now() - DEVICE_PAGINATION.EIGHT_HOURS_MS).toISOString(),
      limit: options?.limit ?? DEVICE_PAGINATION.LIMIT,
    };

    const url = `${this.orgSpecificBaseUrl}/v1/partner/${partnerId}/provider/${provider}/devices/`;
    const headers = this.getHeaders;

    if (includeHeaders) {
      return this._httpClient
        .get<FsPresenceResponse>(url, {
          headers,
          params,
          observe: 'response',
        })
        .pipe(
          tap((response) => {
            this._logPresenceApiResponse(
              {
                service: 'drawer',
                endpoint: 'device_list',
                statusCode: (response as HttpResponse<FsPresenceResponse>).status,
                totalFeatures:
                  (response as HttpResponse<FsPresenceResponse>).body?.features?.length || 0,
                correlationId:
                  (response as HttpResponse<FsPresenceResponse>).headers.get('X-Correlation-ID') ||
                  undefined,
                agencyId: partnerId,
              },
              'drawer-device-list-response',
            );
          }),
        );
    }

    return this._httpClient
      .get<FsPresenceResponse>(url, { headers, params, observe: 'response' })
      .pipe(
        map((res) => {
          this._logPresenceApiResponse(
            {
              service: 'drawer',
              endpoint: 'device_list',
              statusCode: res.status,
              totalFeatures: res.body?.features?.length || 0,
              correlationId: res.headers.get('X-Correlation-ID') || undefined,
              agencyId: partnerId,
            },
            'drawer-device-list-response',
          );
          return this.getResults(res);
        }),
      );
  }

  public parseLinkHeader(linkHeader: string): string {
    if (!linkHeader) {
      return null;
    }
    const parts = linkHeader.split(',');
    for (const part of parts) {
      const section = part.split(';');
      const url = section[0].replace(/<(.*)>/, '$1').trim();
      const params = section[1];
      if (params.includes('rel="next"')) {
        return url;
      }
    }
    return null;
  }

  public getResults(res: HttpResponse<FsPresenceResponse>): FsPresenceResponse {
    const linkHeader = res.headers.get('Link');
    const nextURL = this.parseLinkHeader(linkHeader);
    const results = {
      ...res.body,
      nextURL,
      features: res.body.features.map((feature) => {
        const device = new FsPresenceDevice(feature);
        return device;
      }),
    };
    return results;
  }

  /**
   * @description: This method is used to update the bbox of the map.
   * @param bbox: The bbox of the map.
   */
  public updateBbox(bbox: string[]): void {
    this._bbox$.next(bbox);
  }

  /**
   * @description: This method is used to update the zoom level of the map.
   * @param zoomLevel: The zoom level of the map.
   */
  public updateZoomLevel(zoomLevel: number): void {
    this._zoomLevel$.next(zoomLevel);
  }

  public openPopout(device: IDevice): void {
    this._openPopout$.next(device);
  }

  public enterFollowMode(device: IDeviceMarker): void {
    this.deviceInFollowMode = device;
    this._enterFollowMode$.next(device);
  }

  public leaveFollowMode(device: IDeviceMarker): void {
    this.deviceInFollowMode = undefined;
    this._leaveFollowMode$.next(device);
  }

  public dockBodyCamera(device: IDevice): void {
    this._bodyCameraDocked$.next(device);
  }

  private _logPresenceApiResponse(
    params: {
      service: 'map' | 'drawer';
      endpoint: 'maps_devices' | 'device_list' | 'device_details';
      statusCode: number;
      totalFeatures: number;
      correlationId?: string;
      agencyId: string;
    },
    throttleKey?: ThrottleKey,
  ): void {
    logPresenceApiResponse(
      this._datadogService,
      {
        service: params.service,
        endpoint: params.endpoint,
        statusCode: params.statusCode,
        totalFeatures: params.totalFeatures,
        correlationId: params.correlationId,
        organizationId: this._currentUser.organization.id,
        agencyId: params.agencyId,
        userId: this._currentUser.id,
      },
      throttleKey,
    );
  }
}
